import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Package, Scan, Check, X, Download, RefreshCw, Search, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const DesignTransfer = () => {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('transfer');

  // Transfer mode
  const [transferMode, setTransferMode] = useState('bundle'); // 'bundle' or 'usn'

  // Scan state
  const [scanCode, setScanCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validatedData, setValidatedData] = useState(null);

  // Cascading dropdown state for target FG selection
  const [categories, setCategories] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [descriptions, setDescriptions] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDesign, setSelectedDesign] = useState('');
  const [selectedShade, setSelectedShade] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [targetFGBarcode, setTargetFGBarcode] = useState('');

  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [isDesignsLoading, setIsDesignsLoading] = useState(false);
  const [isShadesLoading, setIsShadesLoading] = useState(false);
  const [isSizesLoading, setIsSizesLoading] = useState(false);
  const [isDescriptionsLoading, setIsDescriptionsLoading] = useState(false);

  // Pending transfer state
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [isCreatingTransfer, setIsCreatingTransfer] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Staff ID
  const [staffId, setStaffId] = useState('');

  // History state
  const [transferHistory, setTransferHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [stats, setStats] = useState(null);

  // Load categories on mount
  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, []);

  // Focus scan input when transfer mode changes
  useEffect(() => {
    if (activeTab === 'transfer' && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [activeTab, transferMode]);

  // Cascading effects
  useEffect(() => {
    if (selectedCategory) {
      fetchDesigns(selectedCategory);
      resetDropdownsFrom('category');
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedDesign) {
      fetchShades(selectedDesign);
      resetDropdownsFrom('design');
    }
  }, [selectedDesign]);

  useEffect(() => {
    if (selectedDesign && selectedShade) {
      fetchSizes(selectedDesign, selectedShade);
      resetDropdownsFrom('shade');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShade]);

  useEffect(() => {
    if (selectedDesign && selectedShade && selectedSize) {
      fetchDescriptions(selectedDesign, selectedShade, selectedSize);
      resetDropdownsFrom('size');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSize]);

  // Load history when tab changes
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const resetDropdownsFrom = (level) => {
    if (level === 'category') {
      setSelectedDesign('');
      setSelectedShade('');
      setSelectedSize('');
      setSelectedDescription('');
      setTargetFGBarcode('');
      setDesigns([]);
      setShades([]);
      setSizes([]);
      setDescriptions([]);
    } else if (level === 'design') {
      setSelectedShade('');
      setSelectedSize('');
      setSelectedDescription('');
      setTargetFGBarcode('');
      setShades([]);
      setSizes([]);
      setDescriptions([]);
    } else if (level === 'shade') {
      setSelectedSize('');
      setSelectedDescription('');
      setTargetFGBarcode('');
      setSizes([]);
      setDescriptions([]);
    } else if (level === 'size') {
      setSelectedDescription('');
      setTargetFGBarcode('');
      setDescriptions([]);
    }
  };

  const fetchCategories = async () => {
    setIsCategoriesLoading(true);
    try {
      const response = await apiClient.get('/design-transfer/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const fetchDesigns = async (category) => {
    setIsDesignsLoading(true);
    try {
      const response = await apiClient.get(`/design-transfer/designs?category4=${encodeURIComponent(category)}`);
      setDesigns(response.data.designs || []);
    } catch (error) {
      toast.error('Failed to load designs');
    } finally {
      setIsDesignsLoading(false);
    }
  };

  const fetchShades = async (design) => {
    setIsShadesLoading(true);
    try {
      const response = await apiClient.get(`/design-transfer/shades?design_name=${encodeURIComponent(design)}`);
      setShades(response.data.shades || []);
    } catch (error) {
      toast.error('Failed to load shades');
    } finally {
      setIsShadesLoading(false);
    }
  };

  const fetchSizes = async (design, shade) => {
    setIsSizesLoading(true);
    try {
      const response = await apiClient.get(`/design-transfer/sizes?design_name=${encodeURIComponent(design)}&shade=${encodeURIComponent(shade)}`);
      setSizes(response.data.sizes || []);
    } catch (error) {
      toast.error('Failed to load sizes');
    } finally {
      setIsSizesLoading(false);
    }
  };

  const fetchDescriptions = async (design, shade, size) => {
    setIsDescriptionsLoading(true);
    try {
      const response = await apiClient.get(`/design-transfer/descriptions?design_name=${encodeURIComponent(design)}&shade=${encodeURIComponent(shade)}&size=${encodeURIComponent(size)}`);
      setDescriptions(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load descriptions');
    } finally {
      setIsDescriptionsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/design-transfer/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchHistory = async () => {
    setIsHistoryLoading(true);
    try {
      let url = '/design-transfer/history?';
      const params = [];
      if (historyDateFrom) params.push(`date_from=${historyDateFrom}`);
      if (historyDateTo) params.push(`date_to=${historyDateTo}`);
      if (historyStatus) params.push(`status=${historyStatus}`);
      url += params.join('&');

      const response = await apiClient.get(url);
      setTransferHistory(response.data.transfers || []);
    } catch (error) {
      toast.error('Failed to load transfer history');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleScan = async () => {
    if (!scanCode.trim()) {
      toast.error('Please enter a scan code');
      return;
    }

    setIsValidating(true);
    try {
      const response = await apiClient.post(`/design-transfer/validate-scan?transfer_mode=${transferMode}&scan_code=${encodeURIComponent(scanCode.trim())}`);
      setValidatedData(response.data);
      toast.success(`Found ${response.data.usn_count} USN(s)`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Validation failed';
      toast.error(message);
      setValidatedData(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleScanKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const handleDescriptionSelect = (barcode) => {
    setSelectedDescription(barcode);
    setTargetFGBarcode(barcode);
  };

  const handleCreateTransfer = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    if (!targetFGBarcode) {
      toast.error('Please select a target FG');
      return;
    }
    if (!validatedData) {
      toast.error('Please scan and validate first');
      return;
    }

    setIsCreatingTransfer(true);
    try {
      const response = await apiClient.post('/design-transfer/create-pending', {
        staff_id: staffId,
        transfer_mode: transferMode,
        scan_code: scanCode,
        target_fg_barcode: targetFGBarcode
      });

      setPendingTransfer(response.data.pending_transfer);
      setShowConfirmDialog(true);
      toast.success('Transfer ready for confirmation');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create transfer';
      toast.error(message);
    } finally {
      setIsCreatingTransfer(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!pendingTransfer) return;

    setIsConfirming(true);
    try {
      const response = await apiClient.post(`/design-transfer/confirm/${pendingTransfer.transfer_id}`);
      toast.success(response.data.message);
      
      // Reset state
      setShowConfirmDialog(false);
      setPendingTransfer(null);
      setValidatedData(null);
      setScanCode('');
      resetDropdownsFrom('category');
      setSelectedCategory('');
      fetchStats();
      
      // Focus back on scan input
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to confirm transfer';
      toast.error(message);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancelTransfer = async () => {
    if (!pendingTransfer) {
      setShowConfirmDialog(false);
      return;
    }

    try {
      await apiClient.post(`/design-transfer/cancel/${pendingTransfer.transfer_id}`);
      toast.info('Transfer cancelled');
    } catch (error) {
      console.error('Error cancelling:', error);
    }
    
    setShowConfirmDialog(false);
    setPendingTransfer(null);
  };

  const handleExportHistory = async () => {
    try {
      let url = '/design-transfer/history/export?';
      const params = [];
      if (historyDateFrom) params.push(`date_from=${historyDateFrom}`);
      if (historyDateTo) params.push(`date_to=${historyDateTo}`);
      if (historyStatus) params.push(`status=${historyStatus}`);
      url += params.join('&');

      const response = await apiClient.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `design_transfers_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export history');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'default',
      pending: 'secondary',
      cancelled: 'destructive'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  // Get selected item details for display
  const selectedTargetItem = descriptions.find(d => d.barcode === targetFGBarcode);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-to-wms-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">Design Transfer</h1>
                <p className="text-sm text-[#E8DCC8]">Transfer USNs to different FG Name</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Total Transfers</p>
                <p className="text-2xl font-bold">{stats.total_transfers}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed_transfers}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="text-2xl font-bold text-blue-600">{stats.today_transfers}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">USNs Transferred</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_usns_transferred}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="transfer" data-testid="transfer-tab">New Transfer</TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">Transfer History</TabsTrigger>
          </TabsList>

          {/* Transfer Tab */}
          <TabsContent value="transfer">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Scan Section */}
              <div className="space-y-6">
                {/* Staff ID */}
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">Staff Identification</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label>Staff ID</Label>
                      <Input
                        placeholder="Enter Staff ID"
                        value={staffId}
                        onChange={(e) => setStaffId(e.target.value)}
                        data-testid="staff-id-input"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Transfer Mode & Scan */}
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">Scan Mode</CardTitle>
                    <CardDescription>Select transfer mode and scan bundle or USN</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Mode Selection */}
                    <div className="flex gap-4">
                      <Button
                        variant={transferMode === 'bundle' ? 'default' : 'outline'}
                        onClick={() => {
                          setTransferMode('bundle');
                          setValidatedData(null);
                          setScanCode('');
                        }}
                        className="flex-1"
                        data-testid="mode-bundle-btn"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Bundle Scan
                      </Button>
                      <Button
                        variant={transferMode === 'usn' ? 'default' : 'outline'}
                        onClick={() => {
                          setTransferMode('usn');
                          setValidatedData(null);
                          setScanCode('');
                        }}
                        className="flex-1"
                        data-testid="mode-usn-btn"
                      >
                        <Scan className="w-4 h-4 mr-2" />
                        USN Scan
                      </Button>
                    </div>

                    {/* Scan Input */}
                    <div className="space-y-2">
                      <Label>{transferMode === 'bundle' ? 'Bundle Barcode' : 'USN Code'}</Label>
                      <div className="flex gap-2">
                        <Input
                          ref={scanInputRef}
                          placeholder={transferMode === 'bundle' ? 'Scan bundle barcode...' : 'Scan USN code...'}
                          value={scanCode}
                          onChange={(e) => setScanCode(e.target.value)}
                          onKeyPress={handleScanKeyPress}
                          data-testid="scan-input"
                        />
                        <Button 
                          onClick={handleScan} 
                          disabled={isValidating || !scanCode.trim()}
                          data-testid="scan-btn"
                        >
                          {isValidating ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Validation Result */}
                    {validatedData && (
                      <div className="border rounded-lg p-4 bg-green-50 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-green-700">Scan Validated</span>
                          <Badge variant="outline">{validatedData.usn_count} USN(s)</Badge>
                        </div>
                        
                        {validatedData.current_fg_info && (
                          <div className="text-sm space-y-1">
                            <p className="font-medium">Current FG:</p>
                            <p className="text-muted-foreground">
                              {validatedData.current_fg_info.design_name} | {validatedData.current_fg_info.shade} | {validatedData.current_fg_info.size}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Barcode: {validatedData.current_fg_info.barcode}
                            </p>
                          </div>
                        )}
                        
                        {validatedData.bundle_info && (
                          <div className="text-sm">
                            <p>Bundle: {validatedData.bundle_info.bundle_code}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Target FG Selection */}
              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg">Target FG Selection</CardTitle>
                    <CardDescription>Select the new FG Name to transfer USNs to</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Category */}
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder={isCategoriesLoading ? "Loading..." : "Select Category"} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Design */}
                    <div className="space-y-2">
                      <Label>Design Name</Label>
                      <Select 
                        value={selectedDesign} 
                        onValueChange={setSelectedDesign}
                        disabled={!selectedCategory}
                      >
                        <SelectTrigger data-testid="design-select">
                          <SelectValue placeholder={isDesignsLoading ? "Loading..." : "Select Design"} />
                        </SelectTrigger>
                        <SelectContent>
                          {designs.map((design) => (
                            <SelectItem key={design} value={design}>{design}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Shade */}
                    <div className="space-y-2">
                      <Label>Shade</Label>
                      <Select 
                        value={selectedShade} 
                        onValueChange={setSelectedShade}
                        disabled={!selectedDesign}
                      >
                        <SelectTrigger data-testid="shade-select">
                          <SelectValue placeholder={isShadesLoading ? "Loading..." : "Select Shade"} />
                        </SelectTrigger>
                        <SelectContent>
                          {shades.map((shade) => (
                            <SelectItem key={shade} value={shade}>{shade}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Size */}
                    <div className="space-y-2">
                      <Label>Size</Label>
                      <Select 
                        value={selectedSize} 
                        onValueChange={setSelectedSize}
                        disabled={!selectedShade}
                      >
                        <SelectTrigger data-testid="size-select">
                          <SelectValue placeholder={isSizesLoading ? "Loading..." : "Select Size"} />
                        </SelectTrigger>
                        <SelectContent>
                          {sizes.map((size) => (
                            <SelectItem key={size} value={size}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label>Description (FG Item)</Label>
                      <Select 
                        value={selectedDescription} 
                        onValueChange={handleDescriptionSelect}
                        disabled={!selectedSize}
                      >
                        <SelectTrigger data-testid="description-select">
                          <SelectValue placeholder={isDescriptionsLoading ? "Loading..." : "Select Description"} />
                        </SelectTrigger>
                        <SelectContent>
                          {descriptions.map((item) => (
                            <SelectItem key={item.barcode} value={item.barcode}>
                              {item.description || item.barcode}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Selected Target FG Display */}
                    {selectedTargetItem && (
                      <div className="border rounded-lg p-4 bg-blue-50 space-y-2">
                        <p className="font-medium text-blue-700">Target FG Selected</p>
                        <div className="text-sm space-y-1">
                          <p><span className="text-muted-foreground">Design:</span> {selectedTargetItem.design_name}</p>
                          <p><span className="text-muted-foreground">Shade:</span> {selectedTargetItem.shade}</p>
                          <p><span className="text-muted-foreground">Size:</span> {selectedTargetItem.size}</p>
                          <p><span className="text-muted-foreground">Barcode:</span> {selectedTargetItem.barcode}</p>
                        </div>
                      </div>
                    )}

                    {/* Transfer Button */}
                    <Button
                      className="w-full"
                      size="lg"
                      disabled={!validatedData || !targetFGBarcode || !staffId || isCreatingTransfer}
                      onClick={handleCreateTransfer}
                      data-testid="create-transfer-btn"
                    >
                      {isCreatingTransfer ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Creating Transfer...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Create Transfer
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle>Transfer History</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="date"
                      placeholder="From"
                      value={historyDateFrom}
                      onChange={(e) => setHistoryDateFrom(e.target.value)}
                      className="w-36"
                      data-testid="history-date-from"
                    />
                    <Input
                      type="date"
                      placeholder="To"
                      value={historyDateTo}
                      onChange={(e) => setHistoryDateTo(e.target.value)}
                      className="w-36"
                      data-testid="history-date-to"
                    />
                    <Select value={historyStatus || "all"} onValueChange={(val) => setHistoryStatus(val === "all" ? "" : val)}>
                      <SelectTrigger className="w-32" data-testid="history-status-select">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={fetchHistory} data-testid="filter-history-btn">
                      <Search className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={handleExportHistory} data-testid="export-history-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isHistoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  </div>
                ) : transferHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No transfer history found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Transfer ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Mode</TableHead>
                          <TableHead>USNs</TableHead>
                          <TableHead>From FG</TableHead>
                          <TableHead>To FG</TableHead>
                          <TableHead>Staff</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transferHistory.map((transfer) => (
                          <TableRow key={transfer.transfer_id}>
                            <TableCell className="font-mono text-xs">{transfer.transfer_id}</TableCell>
                            <TableCell>{getStatusBadge(transfer.status)}</TableCell>
                            <TableCell className="capitalize">{transfer.transfer_mode}</TableCell>
                            <TableCell>{transfer.usn_count}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {transfer.current_fg_info?.design_name || 'N/A'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {transfer.target_fg?.design_name || 'N/A'}
                              </div>
                            </TableCell>
                            <TableCell>{transfer.staff_name || transfer.staff_id}</TableCell>
                            <TableCell className="text-sm">
                              {new Date(transfer.created_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Transfer</DialogTitle>
            <DialogDescription>
              Review the transfer details before confirming.
            </DialogDescription>
          </DialogHeader>
          
          {pendingTransfer && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">USNs to Transfer</p>
                  <p className="font-medium text-lg">{pendingTransfer.usn_count}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Transfer ID</p>
                  <p className="font-mono text-xs">{pendingTransfer.transfer_id}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">From:</p>
                <p className="font-medium">
                  {pendingTransfer.current_fg?.design_name || 'Not Assigned'}
                </p>
                {pendingTransfer.current_fg?.shade && (
                  <p className="text-sm text-muted-foreground">
                    {pendingTransfer.current_fg.shade} | {pendingTransfer.current_fg.size}
                  </p>
                )}
              </div>
              
              <div className="flex items-center justify-center">
                <ArrowRight className="w-6 h-6 text-muted-foreground" />
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">To:</p>
                <p className="font-medium text-blue-600">
                  {pendingTransfer.target_fg?.design_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {pendingTransfer.target_fg?.shade} | {pendingTransfer.target_fg?.size}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelTransfer} disabled={isConfirming}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleConfirmTransfer} disabled={isConfirming} data-testid="confirm-transfer-btn">
              {isConfirming ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Transfer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignTransfer;
